import * as XLSX from "xlsx";
import { supabase } from "../../../lib/supabase";
import {
  surveyReportTokens,
  workCompletionReportTokens,
} from "../../../lib/database";

const CHUNK_SIZE = 100;
const URL_SEP = " | ";

const toText = (v) => (v == null ? "" : String(v));

const yesNo = (v) => {
  if (v === true) return "Yes";
  if (v === false) return "No";
  return "";
};

const formatDate = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleDateString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
  });
};

const formatDateTime = (value) => {
  if (!value) return "";
  return new Date(value).toLocaleString("en-IN", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "2-digit",
    minute: "2-digit",
  });
};

const joinUrls = (urls) =>
  (Array.isArray(urls) ? urls : [])
    .filter((u) => typeof u === "string" && u.trim())
    .join(URL_SEP);

const firstUrl = (value) => {
  if (typeof value === "string" && value.trim()) return value.trim();
  if (Array.isArray(value)) {
    const found = value.find((u) => typeof u === "string" && u.trim());
    return found ? found.trim() : "";
  }
  return "";
};

const feetToMeters = (feetValue) => {
  const feet = Number.parseFloat(feetValue);
  if (!Number.isFinite(feet)) return "";
  return (feet * 0.3048).toFixed(2);
};

const formatCableLength = (feetValue, metersValue) => {
  const meters =
    String(metersValue ?? "").trim() || feetToMeters(feetValue) || "";
  if (!meters) return "";
  return `${meters} m`;
};

const chunkIds = (ids) => {
  const chunks = [];
  for (let i = 0; i < ids.length; i += CHUNK_SIZE) {
    chunks.push(ids.slice(i, i + CHUNK_SIZE));
  }
  return chunks;
};

/**
 * Batch-fetch rows keyed by customer_id.
 * Images are already stored as full Supabase public URLs.
 */
const fetchByCustomerIds = async (table, select, customerIds) => {
  const map = {};
  if (!customerIds?.length) return map;

  for (const chunk of chunkIds(customerIds)) {
    const { data, error } = await supabase
      .from(table)
      .select(select)
      .in("customer_id", chunk);

    if (error) throw error;
    (data || []).forEach((row) => {
      if (row?.customer_id) map[row.customer_id] = row;
    });
  }

  return map;
};

const applyHyperlinks = (ws, exportData, columnKeys) => {
  if (!exportData.length) return;
  const keys = Object.keys(exportData[0]);
  const linkCols = columnKeys
    .map((name) => ({ name, index: keys.indexOf(name) }))
    .filter((c) => c.index >= 0);

  for (let rowIdx = 0; rowIdx < exportData.length; rowIdx++) {
    for (const { name, index } of linkCols) {
      const raw = exportData[rowIdx][name];
      // Only hyperlink single-URL cells (multi-URL cells stay as plain text)
      if (!raw || typeof raw !== "string" || raw.includes(URL_SEP)) continue;
      const cellRef = XLSX.utils.encode_cell({ r: rowIdx + 1, c: index });
      if (ws[cellRef]) {
        ws[cellRef].l = { Target: raw, Tooltip: `Open ${name}` };
      }
    }
  }
};

/**
 * Exports customer + survey/civil/technical/transport/commissioning data to Excel.
 * Photo fields use the stored Supabase storage public URLs (downloadable links).
 * @param {Array} customers
 * @param {Array} regions
 */
export const exportCustomersToExcel = async (customers, regions = []) => {
  if (!customers || customers.length === 0) {
    throw new Error("No customers to export");
  }

  const customerIds = customers.map((c) => c.id);

  const [
    surveyById,
    transportById,
    civilById,
    technicalById,
    commissioningById,
  ] = await Promise.all([
    fetchByCustomerIds("surveys", "*", customerIds),
    fetchByCustomerIds(
      "transport_work",
      "customer_id, customer_kit_photo, panel_structure_photo, notes, approval_status, disapproval_reason, updated_at",
      customerIds,
    ),
    fetchByCustomerIds(
      "civil_work",
      "customer_id, photos, notes, approval_status, disapproval_reason, updated_at",
      customerIds,
    ),
    fetchByCustomerIds(
      "technical_work",
      "customer_id, panels, wiring_inverter, wiring, earthing, testing, installation_photos, customer_photo, voltage_reading, current_reading, installation_completed_at, notes, approval_status, disapproval_reason",
      customerIds,
    ),
    fetchByCustomerIds("commissioning_work", "*", customerIds),
  ]);

  const baseUrl =
    typeof window !== "undefined" && window.location?.origin
      ? window.location.origin
      : "";

  const customerIdsWithSurvey = customerIds.filter((id) => surveyById[id]);
  const customerIdsWithCommissioning = customerIds.filter(
    (id) => commissioningById[id],
  );

  const [surveyReportTokenMap, wcrTokenMap] = await Promise.all([
    surveyReportTokens.createForCustomers(customerIdsWithSurvey),
    workCompletionReportTokens.createForCustomers(customerIdsWithCommissioning),
  ]);

  const exportData = customers.map((customer) => {
    const region = regions.find((r) => r.id === customer.region_id);
    const survey = surveyById[customer.id] || null;
    const transport = transportById[customer.id] || null;
    const civil = civilById[customer.id] || null;
    const technical = technicalById[customer.id] || null;
    const commissioning = commissioningById[customer.id] || null;

    const villageOrRegion =
      [customer.village, region?.name].filter(Boolean).join(" / ") || "";

    const coordinates =
      customer.latitude != null && customer.longitude != null
        ? `${customer.latitude}, ${customer.longitude}`
        : "";

    const feasibility =
      survey?.roof_feasible === true
        ? "Feasible"
        : survey?.roof_feasible === false
          ? "Not Feasible"
          : "";

    const surveyReportToken = surveyReportTokenMap[customer.id];
    const surveyReportLink =
      baseUrl && surveyReportToken
        ? `${baseUrl}/survey-report/${encodeURIComponent(surveyReportToken)}`
        : "";

    const wcrToken = wcrTokenMap[customer.id];
    const wcrLink =
      baseUrl && wcrToken
        ? `${baseUrl}/work-completion-report/${encodeURIComponent(wcrToken)}`
        : "";

    const wiring = technical?.wiring || {};
    const wiringInverter = technical?.wiring_inverter || {};
    const testing = technical?.testing || {};
    const earthing = technical?.earthing || {};
    const panels = technical?.panels || {};
    const installationPhotos = technical?.installation_photos || {};
    const civilPhotos = civil?.photos || {};
    const verification = commissioning?.verification || {};

    const panelPhoto = (id) => {
      const panel = panels[id] || panels[String(id)] || {};
      return (
        panel.serialNumberPhoto ||
        panel.serial_number_photo ||
        panel.photo ||
        ""
      );
    };

    return {
      // —— Customer ——
      CIN: customer.cin || "",
      "Village/Region name": villageOrRegion,
      "Customer Name": customer.name || "",
      "Phone number": customer.phone_number || "",
      "Aadhar number": customer.aadhaar_number || "",
      USN: customer.usn || "",
      Status: customer.status || "",
      Address: customer.address || "",
      District: customer.district || "",
      State: customer.state || "",
      Pincode: customer.pincode || "",
      Coordinates: coordinates,

      // —— Survey ——
      "Survey approval status": survey?.approval_status || "",
      "Roof type": survey?.roof_type || "",
      "Shadow free area":
        survey?.shadow_free_area_sqft != null
          ? String(survey.shadow_free_area_sqft)
          : "",
      "Total roof area":
        survey?.total_roof_area_sqft != null
          ? String(survey.total_roof_area_sqft)
          : "",
      "Panel placement": survey?.panel_placement || "",
      Feasibility: feasibility,
      "Roof rejection reason": survey?.roof_rejection_reason || "",
      "Roof condition": survey?.roof_condition || "",
      "Roof orientation": survey?.roof_orientation || "",
      "Parapet height (ft)":
        survey?.parapet_height_ft != null
          ? String(survey.parapet_height_ft)
          : "",
      "Number of floors":
        survey?.number_of_floors != null ? String(survey.number_of_floors) : "",
      "Access type": survey?.access_type || "",
      "Proposed capacity (kW)":
        survey?.proposed_capacity_kw != null
          ? String(survey.proposed_capacity_kw)
          : "",
      "Sanctioned load (kW)":
        survey?.sanctioned_load_kw != null
          ? String(survey.sanctioned_load_kw)
          : "",
      "Phase connection": survey?.phase_connection || "",
      "Meter location": survey?.meter_location || "",
      "Internet status": survey?.internet_status || "",
      "Earthing soil type": survey?.earthing_soil_type || "",
      "Water tank present": yesNo(survey?.water_tank_present),
      "Neighbors wall present": yesNo(survey?.neighbors_wall_present),
      "Trees present": yesNo(survey?.trees_present),
      "No obstacle": yesNo(survey?.no_obstacle),
      "Survey completed on": formatDate(
        survey?.submitted_at || survey?.updated_at || survey?.created_at,
      ),
      "Lineman name": survey?.lineman_name || "",
      "Survey notes": survey?.survey_notes || "",
      "Survey report": surveyReportLink,
      "Survey - Roof inspection photos": joinUrls(
        survey?.roof_inspection_photos,
      ),
      "Survey - House front view photos": joinUrls(
        survey?.house_front_view_photos,
      ),
      "Survey - Meter photos": joinUrls(survey?.meter_photos),
      "Survey - Line diagram photos": joinUrls(survey?.line_diagram_photos),
      "Survey - Obstacle photos": joinUrls(survey?.obstacle_photos),
      "Survey - QR code photo": firstUrl(survey?.qr_code_photo),
      "Survey - Aadhar photo": firstUrl(survey?.aadhar_photo),
      "Survey - Bank passbook photo": firstUrl(survey?.bank_passbook_photo),
      "Survey - Electricity bill photo": firstUrl(
        survey?.electricity_bill_photo,
      ),
      "Survey - Surveyor signature": firstUrl(survey?.surveyor_signature_photo),
      "Survey - Customer signature": firstUrl(survey?.customer_signature_photo),
      "Survey - Tentative panel location photo": firstUrl(
        survey?.tentative_panel_location_photo,
      ),
      "Survey - Tentative inverter location photo": firstUrl(
        survey?.tentative_inverter_location_photo,
      ),

      // —— Transport ——
      "Transport approval status": transport?.approval_status || "",
      "Transport notes": transport?.notes || "",
      "Transport disapproval reason": transport?.disapproval_reason || "",
      "Transport - Customer kit photo": firstUrl(transport?.customer_kit_photo),
      "Transport - Panel structure photo": firstUrl(
        transport?.panel_structure_photo,
      ),

      // —— Civil ——
      "Civil approval status": civil?.approval_status || "",
      "Civil notes": civil?.notes || "",
      "Civil disapproval reason": civil?.disapproval_reason || "",
      "Civil - Mounting structure photo": firstUrl(
        civilPhotos.mounting_structure,
      ),

      // —— Technical ——
      "Technical approval status": technical?.approval_status || "",
      "Technical notes": technical?.notes || "",
      "Technical disapproval reason": technical?.disapproval_reason || "",
      "Technical completed on": formatDateTime(
        technical?.installation_completed_at,
      ),
      "Inverter make": toText(
        wiringInverter.inverterMake || wiringInverter.inverter_make,
      ),
      "Voltage reading": technical?.voltage_reading || "",
      "Current reading": technical?.current_reading || "",
      "DC black cable": formatCableLength(wiring.dcBlackFeet, null),
      "DC red cable": formatCableLength(wiring.dcRedFeet, null),
      "AC cable": formatCableLength(wiring.acFeet, wiring.acMeters),
      "Earthing cable": formatCableLength(
        wiring.earthingFeet,
        wiring.earthingMeters,
      ),
      "AC/DCDB": toText(
        testing.acdcdb ||
          testing.acDcDb ||
          testing.ac_dcdb ||
          testing.ac_dc_db,
      ),
      "Technical - Panel 1 serial photo": firstUrl(panelPhoto(1)),
      "Technical - Panel 2 serial photo": firstUrl(panelPhoto(2)),
      "Technical - Panel 3 serial photo": firstUrl(panelPhoto(3)),
      "Technical - Panel 4 serial photo": firstUrl(panelPhoto(4)),
      "Technical - Inverter serial photo": firstUrl(
        wiringInverter.inverterSerialPhoto ||
          wiringInverter.inverter_serial_photo,
      ),
      "Technical - Earthing photo 1": firstUrl(earthing.photo1),
      "Technical - Earthing photo 2": firstUrl(earthing.photo2),
      "Technical - Panel side with customer": firstUrl(
        installationPhotos.panelSideWithCustomer,
      ),
      "Technical - Panel front with customer": firstUrl(
        installationPhotos.panelFrontWithCustomer,
      ),
      "Technical - Inverter installation photo": firstUrl(
        installationPhotos.inverterInstallationPhoto,
      ),
      "Technical - Customer photo": firstUrl(technical?.customer_photo),

      // —— Commissioning ——
      "Commissioning approval status": commissioning?.approval_status || "",
      "Commissioning notes": commissioning?.notes || "",
      "Commissioning disapproval reason":
        commissioning?.disapproval_reason || "",
      "Commissioning completed on": formatDateTime(
        commissioning?.commissioning_completed_at,
      ),
      "Panel number 1": toText(commissioning?.panel_number_1),
      "Panel number 2": toText(commissioning?.panel_number_2),
      "Panel number 3": toText(commissioning?.panel_number_3),
      "Panel number 4": toText(commissioning?.panel_number_4),
      "Inverter number": toText(commissioning?.inverter_number),
      "Verification - Cable routing": yesNo(verification.cable_routing),
      "Verification - Lightning arrester": yesNo(
        verification.lightning_arrester_checked,
      ),
      "Verification - Civil structure": yesNo(verification.civil_structure),
      "Verification - Earthing pits": yesNo(verification.earthing_pits),
      "Verification - Inverter functionality": yesNo(
        verification.inverter_functionality_confirmed,
      ),
      "Verification - Safety systems": yesNo(
        verification.safety_systems_operation,
      ),
      "Commissioning - Voltage meter photo": firstUrl(
        commissioning?.voltage_meter_photo,
      ),
      "Commissioning - Main meter serial photo": firstUrl(
        commissioning?.main_meter_serial_photo,
      ),
      "Commissioning - Customer with panels (side)": firstUrl(
        commissioning?.customer_photo_with_panels_side,
      ),
      "Commissioning - Customer with panels (front)": firstUrl(
        commissioning?.customer_photo_with_panels_front,
      ),
      "Commissioning - Customer with inverter": firstUrl(
        commissioning?.customer_photo_with_inverter,
      ),
      "Work completion report": wcrLink,
    };
  });

  const wb = XLSX.utils.book_new();
  const ws = XLSX.utils.json_to_sheet(exportData);

  const hyperlinkColumns = [
    "Survey report",
    "Work completion report",
    "Survey - QR code photo",
    "Survey - Aadhar photo",
    "Survey - Bank passbook photo",
    "Survey - Electricity bill photo",
    "Survey - Surveyor signature",
    "Survey - Customer signature",
    "Survey - Tentative panel location photo",
    "Survey - Tentative inverter location photo",
    "Transport - Customer kit photo",
    "Transport - Panel structure photo",
    "Civil - Mounting structure photo",
    "Technical - Panel 1 serial photo",
    "Technical - Panel 2 serial photo",
    "Technical - Panel 3 serial photo",
    "Technical - Panel 4 serial photo",
    "Technical - Inverter serial photo",
    "Technical - Earthing photo 1",
    "Technical - Earthing photo 2",
    "Technical - Panel side with customer",
    "Technical - Panel front with customer",
    "Technical - Inverter installation photo",
    "Technical - Customer photo",
    "Commissioning - Voltage meter photo",
    "Commissioning - Main meter serial photo",
    "Commissioning - Customer with panels (side)",
    "Commissioning - Customer with panels (front)",
    "Commissioning - Customer with inverter",
  ];
  applyHyperlinks(ws, exportData, hyperlinkColumns);

  const keys = Object.keys(exportData[0] || {});
  ws["!cols"] = keys.map((key) => {
    if (key.toLowerCase().includes("photo") || key.toLowerCase().includes("report")) {
      return { wch: 48 };
    }
    if (key.toLowerCase().includes("notes") || key.toLowerCase().includes("address")) {
      return { wch: 28 };
    }
    return { wch: Math.min(28, Math.max(14, key.length + 2)) };
  });

  XLSX.utils.book_append_sheet(wb, ws, "Customers");

  const timestamp = new Date().toISOString().split("T")[0];
  const filename = `customers_export_${timestamp}.xlsx`;

  XLSX.writeFile(wb, filename);
};
